/** Finite, enclosed widening of existing waterfall receiving reaches. Neither
 * source routes nor ballistic sheets are edited by these derived descriptors. */
import type {RiverFall} from './riverFalls.js';
import {FALL_LIP_RUN_M,RIVER_MAX_CUT_M,riverPointBetween} from './riverFalls.js';
import type {RiverHit,RiverPoint,RiverPolyline} from './rivers.js';
import * as pmath from './pmath.js';

export interface RiverPoolDef {
 readonly id:string;readonly fallId:string;readonly riverId:number;
 readonly startS:number;readonly impactS:number;readonly endS:number;
 /** Same amplitude-unit water level as fall.end.ws. */
 readonly ws:number;
 readonly depthM:number;readonly maxDepthM:number;readonly widthScale:number;
 readonly start:RiverPoint;readonly end:RiverPoint;
}
export interface RiverPoolTerrain {
 readonly amplitude:number;
 /** Native drainage height in amplitude units, before river edits. */
 readonly nativeHeight:(x:number,y:number,z:number)=>number;
 readonly excluded?:(x:number,y:number,z:number)=>boolean;
}
const smooth=(a:number,b:number,x:number):number=>{const t=Math.max(0,Math.min(1,(x-a)/(b-a)));return t*t*(3-2*t)};

export function riverPoolInfluence(pool:RiverPoolDef,s:number):number{
 if(s<=pool.startS||s>=pool.endS)return 0;
 const fadeStart=Math.max(pool.impactS,pool.endS-Math.min(3.2,(pool.endS-pool.startS)*.4));
 return smooth(pool.startS,pool.impactS,s)*(1-smooth(fadeStart,pool.endS,s));
}
export function riverPoolWaterHalf(pool:RiverPoolDef,s:number,originalHalf:number):number{
 return originalHalf*(1+(pool.widthScale-1)*riverPoolInfluence(pool,s));
}
/** Build-time/test frame helper. Hot queries use the graph's existing segment. */
export function riverPoolPoint(pool:RiverPoolDef,s:number):RiverPoint{
 const p=riverPointBetween(pool.start,pool.end,Math.max(0,Math.min(1,(s-pool.startS)/(pool.endS-pool.startS))));
 // Keep the source endpoint bed interpolation: depthM belongs to the new bowl,
 // not to the route/ribbon point contract.
 p.ws=pool.ws;
 return p;
}

/** Validate every prospective pool against native support and existing graph
 * ownership. No rejection raises levees or rewrites an incompatible reach. */
export function buildRiverPools(rivers:readonly RiverPolyline[],falls:readonly RiverFall[],radius:number,terrain:RiverPoolTerrain,hitAt:(x:number,y:number,z:number)=>RiverHit|null):RiverPoolDef[]{
 const pools:RiverPoolDef[]=[],A=terrain.amplitude;
 if(!(A>0))return pools;
 for(const fall of falls){
  const route=rivers.find(r=>r.id===fall.riverId);if(!route||route.canyonId)continue;
  const startS=fall.lip.s+FALL_LIP_RUN_M,impactS=fall.lip.s+fall.runM;
  if(impactS<=startS+.35||fall.end.s<=impactS+.75)continue;
  const start=riverPointBetween(fall.lip,fall.end,FALL_LIP_RUN_M/(fall.end.s-fall.lip.s));start.ws=fall.end.ws;start.bed=fall.end.bed;
  const depthM=4.2+Math.min(1.2,fall.dropM*.07);
  const pool:RiverPoolDef={id:`${fall.id}:pool`,fallId:fall.id,riverId:fall.riverId,startS,impactS,endS:fall.end.s,ws:fall.end.ws,depthM,maxDepthM:depthM+.75,widthScale:2.05,start,end:{...fall.end}};
  let valid=true;
  for(let row=0;row<=8&&valid;row++){
   const s=startS+(fall.end.s-startS)*row/8,p=riverPoolPoint(pool,s),side=[p.fy*p.z-p.fz*p.y,p.fz*p.x-p.fx*p.z,p.fx*p.y-p.fy*p.x],length=pmath.hypot(side[0],side[1],side[2]);
   if(length<1e-9){valid=false;break;}
   const half=riverPoolWaterHalf(pool,s,p.half);
   for(const sign of[-1,1])for(const distance of[p.half*.5,half*.85,p.half*2.6]){
    const off=sign*distance/(radius*length),x=p.x+side[0]*off,y=p.y+side[1]*off,z=p.z+side[2]*off,n=pmath.hypot(x,y,z),dx=x/n,dy=y/n,dz=z/n;
    const hit=hitAt(dx,dy,dz),h=terrain.nativeHeight(dx,dy,dz),heightM=(h-pool.ws)*A;
    if(terrain.excluded?.(dx,dy,dz)||!hit||hit.riverId!==pool.riverId||Math.abs(hit.s-s)>.5||Math.abs(hit.ws-pool.ws)*A>.025){valid=false;break;}
    // A real dry outer rim is mandatory. A downhill shoulder cannot become a
    // suspended water shelf; rejected pools retain the original river shape.
    if(distance===p.half*2.6){if(heightM<.12){valid=false;break;}}
    else if(heightM < -pool.maxDepthM || heightM+.5>RIVER_MAX_CUT_M-.15){valid=false;break;}
   }
  }
  if(valid)pools.push(pool);
 }
 return pools;
}

/** Lower only inside the original carve footprint. A quiet bowl reaches its
 * wide wet edge at effectiveWaterHalf and blends into an untouched dry rim. */
export function carveRiverPoolHeight(nativeH:number,carvedH:number,hit:RiverHit,A:number):number{
 const pool=hit.pool,k=hit.poolInfluence??0;
 if(!(A>0)||hit.landformsEnabled===false||!pool||k<=0||hit.dist>=hit.carveHalf)return carvedH;
 const half=hit.effectiveWaterHalf??hit.waterHalf,x=hit.dist/half;
 const target=hit.ws+(-pool.depthM*(1-smooth(.35,1,x))+.65*smooth(1,1.18,x))/A;
 const edge=1-smooth(half,hit.carveHalf,hit.dist),lower=Math.min(carvedH,target);
 return Math.max(nativeH-RIVER_MAX_CUT_M/A,Math.min(nativeH,carvedH+(lower-carvedH)*k*edge));
}

/** Potential water ownership, including dry-bank provenance for riparian
 * queries. This is NOT a wetness test: renderers additionally require h<ws.
 * Ordinary hits retain their original width-only contract. A verified pool
 * never claims an unexpectedly deep unsupported lateral shoulder. */
export function riverWaterClaim(hit:RiverHit,finalH:number,A:number):boolean{
 if(hit.dist>(hit.claimHalf??hit.carveHalf))return false;
 return !(hit.pool&&(hit.poolInfluence??0)>0&&hit.dist>hit.waterHalf&&(hit.ws-finalH)*A>hit.pool.maxDepthM);
}
